import axios from "axios";
import { baseUrl } from "../config";
import { toast } from "react-toastify";
import { API_TIMEOUTS } from "../constants/app.constants";

export const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: API_TIMEOUTS.DEFAULT, // 30 seconds

  // Do not set a default Content-Type so multipart/form-data requests
  // can have the browser/axios set the correct boundary automatically.
});

// ─────────────────────────────────────────────────────────────────────────────
// SESSION EXPIRY GUARD
//
// Prevents duplicate toasts and redirects when multiple API requests fail with
// 401 simultaneously (e.g. parallel requests on page load).
//
// Problem without guard:
//   - Request interceptor detects expired token → fires toast + schedules redirect
//   - Response interceptor catches the 401 from server → fires toast AGAIN
//   - Result: 2 toasts, 2 redirects
// ─────────────────────────────────────────────────────────────────────────────

let isHandlingSessionExpiry = false;

/**
 * Mark that session expiry is being handled.
 * Auto-resets after 5 seconds as a safety net (in case redirect doesn't fire).
 */
const markSessionExpiryHandled = (): void => {
  isHandlingSessionExpiry = true;
  setTimeout(() => {
    isHandlingSessionExpiry = false;
  }, 5000);
};

// ─────────────────────────────────────────────────────────────────────────────
// ENDPOINT CLASSIFIERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Public endpoints that never require authentication.
 * 401 on these is ignored entirely.
 */
const isPublicEndpoint = (url: string): boolean =>
  url.includes("/api/public/") ||
  url.includes("/customer/") ||
  url.includes("/qr/");

/**
 * Auth initialization endpoints (profile fetch, token validate).
 *
 * A 401 on these means the user's session has expired or they were never
 * logged in. This is NOT an "action mid-session" expiry scenario — it happens
 * silently on app startup.
 *
 * We deliberately skip the "Session expired" toast for these calls because:
 *   1. sessionManager already handles cleanup (clears storage, sets isAuthenticated=false)
 *   2. ProtectedRoute redirects to /login if the user is on a protected page
 *   3. Public pages (homepage, etc.) should keep showing without interruption
 *
 * Showing a toast/redirect here causes the user to be kicked off the public
 * homepage unnecessarily.
 */
const isAuthInitEndpoint = (url: string): boolean =>
  url.includes("api/Auth/profile") ||
  url.includes("api/Auth/validate-token") ||
  url.includes("api/Auth/login") ||       // Login failures are NOT session expiry
  url.includes("api/Auth/register") ||    // Registration errors are NOT session expiry
  url.includes("api/Auth/forgot-password") || // Password reset is NOT session expiry
  url.includes("api/Auth/reset-password") ||  // Password reset is NOT session expiry
  url.includes("api/Auth/staff-login");   // Staff login failures are NOT session expiry

/**
 * Payment endpoints where 401 should surface as a payment error, not "Session expired".
 * These are called during an active checkout flow — if auth fails, the caller's own
 * catch block handles it with a payment-specific error message.
 */
const isPaymentEndpoint = (url: string): boolean =>
  url.includes("/api/payment/phonepe/initiate");

// ─────────────────────────────────────────────────────────────────────────────
// REQUEST INTERCEPTOR
// Add Authorization header + catch locally-detectable expired tokens
// ─────────────────────────────────────────────────────────────────────────────

axiosInstance.interceptors.request.use(
  (config) => {
    // Skip authentication for public endpoints
    if (config.url && isPublicEndpoint(config.url)) {
      return config;
    }

    // Check for both admin token and captain token
    const adminToken = localStorage.getItem("token");
    const captainToken = localStorage.getItem("captainToken");

    // Default to admin token, but use captain token for captain-specific endpoints
    let token = adminToken || captainToken;

    if (
      config.url &&
      (config.url.includes("/captain/") ||
        config.url.includes("/api/captain/")) &&
      captainToken
    ) {
      token = captainToken;
    }

    if (token) {
      // Check if token is expired before making the request
      try {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenPayload.exp && tokenPayload.exp < currentTime) {
          // Token is locally detected as expired
          const isManualLogout = sessionStorage.getItem("manual_logout");
          const isCaptain = !!captainToken && !adminToken;
          const currentPath = window.location.pathname;
          const isLoginPage =
            currentPath === "/login" || currentPath === "/captain/login";

          // Only handle if not already being handled (deduplication guard)
          if (!isHandlingSessionExpiry) {
            markSessionExpiryHandled();

            if (isCaptain) {
              localStorage.removeItem("captainUser");
              localStorage.removeItem("captainToken");
              if (!isManualLogout && !isLoginPage) {
                toast.error("Session expired. Please login again.", {
                  position: "top-center",
                  autoClose: 3000,
                });
              }
              if (!isLoginPage) {
                setTimeout(
                  () => {
                    window.location.href = "/captain/login";
                  },
                  isManualLogout ? 0 : 1000,
                );
              }
            } else {
              localStorage.removeItem("token");
              localStorage.removeItem("authToken");
              localStorage.removeItem("role");
              localStorage.removeItem("userRoles");
              localStorage.removeItem("permissions");
              localStorage.removeItem("userId");
              if (!isManualLogout && !isLoginPage) {
                toast.error("Session expired. Please login again.", {
                  position: "top-center",
                  autoClose: 3000,
                });
              }
              if (!isLoginPage) {
                setTimeout(
                  () => {
                    window.location.href = "/login";
                  },
                  isManualLogout ? 0 : 1000,
                );
              }
            }
          }

          sessionStorage.removeItem("manual_logout");
          return Promise.reject(new Error("Token expired"));
        }

        // Token is valid — add to headers
        if (config.headers) {
          config.headers.Authorization = `Bearer ${token}`;

          // Add X-Tenant-ID header for multi-tenant support
          const tenantId = tokenPayload.tenant_id || tokenPayload.tenantId;
          if (tenantId !== undefined && tenantId !== null) {
            config.headers["X-Tenant-ID"] = tenantId.toString();
          } else {
            // Fallback to businessId from localStorage
            const businessId = localStorage.getItem("businessId");
            if (businessId) {
              config.headers["X-Tenant-ID"] = businessId;
            }
          }
        }
      } catch (error) {
        // Invalid token format — clear it and reject
        const isCaptain = !!captainToken && !adminToken;
        const currentPath = window.location.pathname;
        const isLoginPage =
          currentPath === "/login" || currentPath === "/captain/login";

        if (isCaptain) {
          localStorage.removeItem("captainUser");
          localStorage.removeItem("captainToken");
          if (!isLoginPage) window.location.href = "/captain/login";
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("authToken");
          if (!isLoginPage) window.location.href = "/login";
        }
        return Promise.reject(new Error("Invalid token"));
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// RESPONSE INTERCEPTOR
// Handle 401 Unauthorized from backend
// ─────────────────────────────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // ── Public endpoint errors: just log and reject ────────────────────────
    if (error.config?.url && isPublicEndpoint(error.config.url)) {
      console.error("Public API Error:", {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        url: error.config.url,
      });
      return Promise.reject(error);
    }

    // ── Handle 401 Unauthorized ────────────────────────────────────────────
    if (error.response?.status === 401) {
      // Manual logout: session storage flag set by ProfileDropdown
      const isManualLogout = sessionStorage.getItem("manual_logout");
      if (isManualLogout) {
        sessionStorage.removeItem("manual_logout");
        return Promise.reject(error);
      }

      const currentPath = window.location.pathname;
      const isLoginPage =
        currentPath === "/login" || currentPath === "/captain/login";

      // ── Auth init endpoints (profile fetch / token validate) ─────────────
      //
      // A 401 here means the stored session has expired server-side, or the
      // user was never logged in. This happens silently on app startup.
      //
      // We do NOT show a "Session expired" toast because:
      //   • The user may be on a PUBLIC page (homepage) and should stay there
      //   • sessionManager.fetchFromBackend() catches this error and calls
      //     clearSession(), which sets isAuthenticated=false
      //   • ProtectedRoute automatically redirects to /login if the user
      //     was actually on a protected page
      //
      // This is the PRIMARY fix for the "Session expired" popup on homepage.
      if (error.config?.url && isAuthInitEndpoint(error.config.url)) {
        return Promise.reject(error);
      }

      // ── Payment endpoints: surface as payment error, not session expiry ───
      // Caller's catch block handles it with a payment-specific message.
      if (error.config?.url && isPaymentEndpoint(error.config.url)) {
        return Promise.reject(error);
      }

      // ── Duplicate guard: already handling session expiry ─────────────────
      // Multiple parallel requests can all fail with 401 — only handle once
      if (isHandlingSessionExpiry) {
        return Promise.reject(error);
      }

      // ── No valid token in storage ─────────────────────────────────────────
      //
      // If no token exists at this point (or the token is locally expired),
      // the user is simply not logged in. We must NOT show "Session expired"
      // because:
      //   • The user may be a visitor on public pages (homepage, pricing, etc.)
      //   • Any component that auto-fires on mount (e.g. birthday check, loyalty
      //     customer list) would generate 401s and wrongly kick the user to /login
      //   • "Session expired" implies the user HAD an active session — if they
      //     have no valid token, nothing expired mid-action
      //
      // Silent reject here lets the component's own error handler deal with it.
      const storedAdminToken = localStorage.getItem("token");
      const storedCaptainToken = localStorage.getItem("captainToken");
      const hasAnyToken = !!storedAdminToken || !!storedCaptainToken;

      // Additionally validate that the stored token hasn't expired locally
      let isStoredTokenLocallyValid = false;
      if (storedAdminToken) {
        try {
          const payload = JSON.parse(atob(storedAdminToken.split(".")[1]));
          isStoredTokenLocallyValid =
            !payload.exp || payload.exp * 1000 > Date.now();
        } catch {
          isStoredTokenLocallyValid = false;
        }
      } else if (storedCaptainToken) {
        try {
          const payload = JSON.parse(atob(storedCaptainToken.split(".")[1]));
          isStoredTokenLocallyValid =
            !payload.exp || payload.exp * 1000 > Date.now();
        } catch {
          isStoredTokenLocallyValid = false;
        }
      }

      // No valid token → user is unauthenticated / expired → silent reject
      if (!hasAnyToken || !isStoredTokenLocallyValid) {
        return Promise.reject(error);
      }

      // ── First real 401 on an authenticated action ────────────────────────
      // User HAD a locally-valid token that the server rejected (revoked, etc.)
      // This is a genuine "session expired" scenario — show toast + redirect
      markSessionExpiryHandled();

      const captainToken = localStorage.getItem("captainToken");
      const isCaptain = !!captainToken;

      if (!isLoginPage) {
        toast.error("Session expired. Please login again.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
      }

      if (isCaptain) {
        localStorage.removeItem("captainUser");
        localStorage.removeItem("captainToken");
        if (!isLoginPage) {
          setTimeout(() => {
            window.location.href = "/captain/login";
          }, 1000);
        }
      } else {
        // Clear all admin authentication data
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("role");
        localStorage.removeItem("userRoles");
        localStorage.removeItem("plan");
        localStorage.removeItem("plan_status");
        localStorage.removeItem("hasActiveSubscription");
        localStorage.removeItem("daysRemaining");
        localStorage.removeItem("permissions");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("userPhone");
        localStorage.removeItem("userName");
        localStorage.removeItem("userFirstName");
        localStorage.removeItem("userLastName");
        localStorage.removeItem("userFullName");
        localStorage.removeItem("userId");
        localStorage.removeItem("businessId");
        localStorage.removeItem("business_name");
        localStorage.removeItem("restaurant_name");
        localStorage.removeItem("customerToken");
        localStorage.removeItem("customerId");

        if (!isLoginPage) {
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      }
    }

    return Promise.reject(error);
  },
);
